import { Component, Input, OnInit, Signal, computed, signal } from '@angular/core';
import {
  AppService,
  DentalReferralForm,
  GetReferralHistoryRequest,
  GetReferralHistoryResponse,
  VersionChanges,
  ChangedField
} from '../../app.service';
import { AlertService } from '../alert.service';
import { LoadingService } from '../loading.service';
import { map, catchError, of, finalize } from 'rxjs';

type VersionVM = {
  atUtc: Date;
  atLocal: Date;
  actorId?: string | null;
  actorName?: string | null;
  summary: string;
  changes: FieldChangeVM[];
};

type FieldChangeVM = {
  key: string;
  label: string;
  oldText: string | null;
  newText: string | null;
  changed: boolean;
};

@Component({
  selector: 'app-referral-history',
  templateUrl: './referral-history.component.html',
  styleUrls: ['./referral-history.component.scss']
})
export class ReferralHistoryComponent implements OnInit {
  @Input() referral!: DentalReferralForm;
  /** Optional GUID -> Display Name map until API ships names */
  @Input() userDisplayMap: Record<string, string> = {};

  // Raw from API
  private versions = signal<VersionChanges[]>([]);
  // View models
  vm = signal<VersionVM[]>([]);
  // UI state
  loading = signal<boolean>(false);
  error = signal<string | null>(null);

  // Filters
  query = signal<string>('');
  showOnlyChanged = signal<boolean>(true); // default ON

  // Keep track of which panels are expanded (by stable key)
  private expandedKeys = signal<Set<string>>(new Set<string>());

  // Derived (filtered) view; do not touch expandedKeys here
  filtered = computed<VersionVM[]>(() => {
    const q = this.query().trim().toLowerCase();
    const onlyChanged = this.showOnlyChanged();
    const list = this.vm();

    if (!q && !onlyChanged) return list;

    return list
      .map(v => {
        const changes = v.changes.filter(c => {
          const matchesQuery = !q ||
            c.label.toLowerCase().includes(q) ||
            (c.oldText ?? '').toLowerCase().includes(q) ||
            (c.newText ?? '').toLowerCase().includes(q) ||
            (v.actorName ?? '').toLowerCase().includes(q) ||
            (v.summary ?? '').toLowerCase().includes(q);
          const matchesChanged = !onlyChanged || c.changed;
          return matchesQuery && matchesChanged;
        });
        return { ...v, changes };
      })
      .filter(v => v.changes.length > 0);
  });

  constructor(
    private service: AppService,
    private alert: AlertService,
    private spinner: LoadingService
  ) { }

  ngOnInit(): void {
    if (this.referral?.id) this.load();
  }

  refresh(): void { this.load(); }

  /* ----- Expansion state helpers (preserve across filter changes) ----- */

  keyFor(v: VersionVM): string {
    // Use a stable, unique identifier for a version. If you later add versionId, use that.
    return v.atUtc.toISOString(); // versionStartTime is unique enough in your data
  }

  isExpanded(v: VersionVM): boolean {
    return this.expandedKeys().has(this.keyFor(v));
  }

  onOpened(v: VersionVM): void {
    const k = this.keyFor(v);
    this.expandedKeys.update(s => {
      const next = new Set(s);
      next.add(k);
      return next;
    });
  }

  onClosed(v: VersionVM): void {
    const k = this.keyFor(v);
    this.expandedKeys.update(s => {
      const next = new Set(s);
      next.delete(k);
      return next;
    });
  }

  expandAll(): void {
    const keys = this.filtered().map(v => this.keyFor(v));
    this.expandedKeys.update(s => {
      const next = new Set(s);
      keys.forEach(k => next.add(k));
      return next;
    });
  }

  collapseAll(): void {
    const visible = new Set(this.filtered().map(v => this.keyFor(v)));
    this.expandedKeys.update(s => {
      const next = new Set(s);
      // remove only currently visible ones; keep others for when filter toggles back
      visible.forEach(k => next.delete(k));
      return next;
    });
  }

  trackPanel = (_: number, v: VersionVM) => this.keyFor(v);

  /* -------------------- Data load & VM mapping -------------------- */

  private load(): void {
    const req = new GetReferralHistoryRequest();
    req.referralId = this.referral.id;
    req.referralType = this.referral.referralType;

    this.loading.set(true);
    this.error.set(null);

    this.service.GetReferralHistory(req).pipe(
      map((res: GetReferralHistoryResponse) => {
        if (!res.success) throw new Error(res.message || 'Failed to load history.');
        return res.versionChanges || [];
      }),
      catchError(err => {
        console.error(err);
        this.error.set('Unable to load change history.');
        return of<VersionChanges[]>([]);
      }),
      finalize(() => this.loading.set(false))
    ).subscribe(list => {
      // Keep expandedKeys; new VMs will reuse the same keys if matching versions exist
      this.versions.set(list);
      this.vm.set(this.buildVM(list));
    });
  }

  private buildVM(list: VersionChanges[]): VersionVM[] {
    const sorted = [...list].sort((a, b) =>
      new Date(b.versionStartTime).getTime() - new Date(a.versionStartTime).getTime()
    );

    return sorted.map(v => {
      const atUtc = new Date(v.versionStartTime);
      const atLocal = v.versionStartTimePst ? new Date(v.versionStartTimePst) : atUtc;

      const actorId = this.tryExtractActorId(v.changedFields);
      const actorName = this.resolveActorName(actorId);

      const changes = (v.changedFields || []).map(cf => this.toFieldChangeVM(cf));
      const summary = this.summarize(changes);

      return { atUtc, atLocal, actorId, actorName, summary, changes };
    });
  }

  private toFieldChangeVM(cf: ChangedField): FieldChangeVM {
    const label = this.humanize(cf.propertyName);
    const oldText = this.formatValue(cf.oldValue, cf.propertyName);
    const newText = this.formatValue(cf.newValue, cf.propertyName);
    const changed = (oldText ?? '') !== (newText ?? '');
    return { key: cf.propertyName, label, oldText, newText, changed };
  }

  private summarize(changes: FieldChangeVM[]): string {
    if (!changes?.length) return 'No details';
    const top = changes.slice(0, 3).map(c => c.label).join(', ');
    return changes.length > 3 ? `${top} +${changes.length - 3} more` : top;
  }

  private tryExtractActorId(fields: ChangedField[] | undefined): string | null {
    if (!fields?.length) return null;
    const keys = ['modifiedByUserId', 'createdByUserId', 'reviewedByUserId', 'assignedToUserId'];
    for (const k of keys) {
      const hit = fields.find(f => f.propertyName === k);
      if (hit?.newValue) return String(hit.newValue);
    }
    return null;
  }

  private resolveActorName(actorId: string | null | undefined): string | null {
    if (!actorId) return null;
    const byMap = this.userDisplayMap[actorId];
    if (byMap) return byMap;
    if (this.looksLikeGuid(actorId)) return this.shortGuid(actorId);
    return actorId;
  }

  private looksLikeGuid(v: string): boolean { return /^[0-9a-fA-F-]{32,36}$/.test(v); }
  private shortGuid(v: string): string { return v.length > 8 ? `${v.slice(0, 8)}…` : v; }

  private humanize(prop: string): string {
    
    const overrides: Record<string, string> = {
      referralStatus: 'Referral Status',
      referralReason: 'Referral Reason',
      ModifiedByUserId: 'Modified By',
      CreatedByUserId: 'Created By',
      LockedByUserId: 'Locked By',
      reviewedByUserId: 'Reviewed By',
      assignedToUserId: 'Assigned To',
      dcoId: 'DCO',
      DentalCareOrganizationId: 'DCO',
      Cco: 'CCO',
      medicaidId: 'Medicaid ID',
      memberDob: 'Member DOB',
      providerNpi: 'Provider NPI',
      Edd: 'Expected Delivery Date'
    };
    if (overrides[prop]) return overrides[prop];
    return prop
      .replace(/[_-]/g, ' ')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/^./, s => s.toUpperCase());
  }

  private formatValue(v: any, prop?: string): string | null {
    if (v === null || v === undefined || v === '') return null;
    if (prop?.toLowerCase().includes('date') || v instanceof Date) {
      const d = v instanceof Date ? v : new Date(v);
      if (!isNaN(d.getTime())) return d.toLocaleString();
    }
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    if (typeof v === 'object') {
      try { return JSON.stringify(v); } catch { return String(v); }
    }
    return String(v);
  }
}
